import React from "react";
import { ISavedSearchItem } from "../save-search/save-search.types";
import { ResponsiveLayout, Box } from "@bca/ui-library";

import { VehicleSearchResults } from "../vehicle-search-details/vehicleSearch.type";
import { ViewportSizeType } from "../hooks/useViewport.hook";
import EllipsisPopover from "../ellipsis-popover/EllipsisPopover";
import SaveThisSearchButtonWithState from "../save-search/SaveThisSearch";
import SortingOptions from "../SortingOptions";
import ClearAllFiltersButton from "./ClearAllFiltersButton";
import ResultCount from "./ResultCount";
import ToggleFiltersButton from "./ToggleFiltersButton";
import { useRouter } from "next/router";
import getStockAlertLink from "../getStockAlertLink";
import { TRACKED } from "../constants";

interface TopActionsBarDesktopProps {
  showFilters: boolean;
  onClickShowFilters: (boolean) => void;
  currentPage: number;
  updateUrl: (string) => void;
  searchResults: VehicleSearchResults;
  viewportWidth: ViewportSizeType;
  handleOnClearAllFilters: () => void;
  savedSearches: ISavedSearchItem[];
  filterCount: number;
  allAppliedFilters: any;
  totalTracked: number;
}

const TopActionsBarDesktop: React.FunctionComponent<
  TopActionsBarDesktopProps
> = ({
  showFilters,
  onClickShowFilters,
  currentPage,
  updateUrl,
  searchResults,
  viewportWidth,
  handleOnClearAllFilters,
  savedSearches,
  filterCount,
  allAppliedFilters,
  totalTracked,
}) => {
  const router = useRouter();
  const allQueryStrings = router.query;
  const { requestOrigin } = allQueryStrings;

  const isTrackedPage = requestOrigin === TRACKED;

  const stockAlertLink = getStockAlertLink(allQueryStrings);
  return (
    <ResponsiveLayout
      display="flex"
      flexDirection="row"
      justifyContent="space-between"
      mt={5}
      mb={3}
      css={`
        gap: 43px;
      `}
    >
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="flex-start"
        alignItems="center"
        flexGrow={1}
        maxWidth={viewportWidth === "desktop" && showFilters && "284px"}
      >
        <ToggleFiltersButton
          showFilters={showFilters}
          onClick={onClickShowFilters}
          viewportWidth={viewportWidth}
        />
        <Box
          ml={5}
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
        >
          {!showFilters && (
            <>
              <ResultCount
                resultCount={searchResults?.totalResults}
                totalTracked={totalTracked}
                isTrackedPage={isTrackedPage}
                page={currentPage}
                pageSize={50}
                mx={0}
                mr={5}
              />
              {filterCount !== 0 && !isTrackedPage && (
                <SaveThisSearchButtonWithState
                  savedSearches={savedSearches}
                  viewport={viewportWidth}
                />
              )}
            </>
          )}
        </Box>
        {viewportWidth === "desktop" && showFilters && (
          <ClearAllFiltersButton
            filtersCount={filterCount}
            onClearAllFilters={handleOnClearAllFilters}
          />
        )}
      </Box>
      <Box
        display="flex"
        flexDirection="row"
        flexGrow={3}
        justifyContent="space-between"
      >
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
        >
          {showFilters && (
            <>
              <ResultCount
                resultCount={searchResults?.totalResults}
                isTrackedPage={isTrackedPage}
                totalTracked={totalTracked}
                page={currentPage}
                pageSize={50}
                mx={0}
                mr={5}
              />
              {filterCount !== 0 && !isTrackedPage && (
                <SaveThisSearchButtonWithState
                  savedSearches={savedSearches}
                  viewport={viewportWidth}
                />
              )}
            </>
          )}
        </Box>
        <Box display="flex" flexDirection="row" alignItems="center">
          <SortingOptions
            optionList={searchResults?.sortOptions}
            updateUrl={updateUrl}
            allAppliedFilters={allAppliedFilters}
          ></SortingOptions>
          <EllipsisPopover
            createStockAlertLink={stockAlertLink.toString()}
            printAllLink={
              isTrackedPage
                ? searchResults?.trackedVehiclesWithGuidePricingUrl
                : searchResults?.printAllUrl
            }
            exportAllLink={searchResults?.exportAllUrl}
            pdfEnabled={searchResults?.displayFlags.buyerApiPDFsEnabled}
          />
        </Box>
      </Box>
    </ResponsiveLayout>
  );
};

export default TopActionsBarDesktop;
